import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Lozenge from '@atlaskit/lozenge/new/lozenge';

import { NO_DATA_PLACEHOLDER } from '../common/AnalyticsTable/index.ts';

const i18n = defineMessages({
	engagedLozengeLabel: {
		id: 'confluence-analytics.engagement-cell.engaged',
		defaultMessage: 'Engaged',
		description:
			'Label for the lozenge displayed in the engagement cell of the page views table when a user has met engagement criteria with a content piece.',
	},
	briefLozengeLabel: {
		id: 'confluence-analytics.engagement-cell.brief',
		defaultMessage: 'Brief',
		description:
			'Label for the lozenge displayed in the engagement cell of the page views table when a user has not met engagement criteria with a content piece.',
	},
});

const styles = cssMap({ inset: { paddingLeft: token('space.500') } });

type EngagementCellProps = {
	appearance?: 'lozenge' | 'text';
	lastViewDate?: string;
	isEngaged?: boolean | null;
	inset?: boolean;
};

export const EngagementCell = ({
	appearance = 'lozenge',
	isEngaged,
	lastViewDate,
	inset = true,
}: EngagementCellProps): React.JSX.Element | null => {
	if (appearance === 'text') {
		return lastViewDate ? (
			<Text size="small" color="color.text.subtlest">
				<FormattedMessage {...(isEngaged ? i18n.engagedLozengeLabel : i18n.briefLozengeLabel)} />
			</Text>
		) : null;
	}
	const cellContent = !lastViewDate ? (
		<Text color="color.text.disabled">{NO_DATA_PLACEHOLDER}</Text>
	) : (
		<Lozenge appearance={isEngaged ? 'information' : 'warning'}>
			<FormattedMessage {...(isEngaged ? i18n.engagedLozengeLabel : i18n.briefLozengeLabel)} />
		</Lozenge>
	);

	return (
		<Box
			xcss={inset ? styles.inset : undefined}
			testId={inset ? 'engagement-cell' : 'engagement-cell-flush'}
		>
			{cellContent}
		</Box>
	);
};
